import type { ExploreCluster, ExplorePin } from '../../services/exploreApi';
import './ExploreSurface.css';

/**
 * A pin, and a cluster. Deliberately two builders and not one with a flag.
 *
 * §1.2: a pin's number counts PLANS and a cluster's counts PLACES. Rendering
 * them alike would make "9" mean two things on one screen, and a reader has no
 * way to tell which they are looking at. Separate shapes, separate builders,
 * and the backend keeps them in separate response keys for the same reason.
 *
 * **A pin never reads 0.** A guide pin carries no count at all — the API omits
 * the key rather than sending null or zero — so there is no branch here that
 * could render one. The absence is the guarantee, not a check.
 *
 * These are DOM elements handed to the engine's `Marker`, not React
 * components. An earlier version was a pair of React buttons that `MapSurface`
 * never rendered: they were imported by nothing but their own passing tests,
 * which is precisely the "tests passing does not mean reachable" shape this
 * repository has recorded before. A marker element is what the map can
 * actually draw, so this is the form that can be mounted.
 *
 * **Each one IS a `<button>`** (MapLibre plan S3). Under Leaflet the marker was
 * a focusable wrapper around a labelled `role="img"` span; the engine places
 * the element it is given, so the element is the control: focusable by
 * default, named by its own `aria-label`, announced as a button.
 */
export function pinLabel(pin: ExplorePin): string {
  return pin.state === 'numbered'
    ? `${pin.name}, ${pin.count} ${pin.count === 1 ? 'plan' : 'plans'}`
    : `${pin.name}, guide`;
}

export function clusterLabel(cluster: ExploreCluster): string {
  // "places", never "plans". The word is the only thing distinguishing this
  // number from a pin's for someone who cannot see the shape — and §1.2 asks
  // for it on screen too, not only in the accessible name.
  // "1 places" is what a bare plural gives you, and a cluster of one is
  // reachable: the grid is coarse on purpose, so a cell often holds one.
  return `${cluster.places} ${cluster.places === 1 ? 'place' : 'places'}`;
}

/** A button carrying its accessible name; the visible content is the shape. */
function markerButton(className: string, label: string): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = className;
  // The name is the label, not the glyphs inside: "2" or "✦" alone names
  // nothing. `textContent` and `setAttribute` never parse markup, so a name
  // from the database cannot inject any.
  button.setAttribute('aria-label', label);
  return button;
}

function span(className: string, text: string): HTMLSpanElement {
  const el = document.createElement('span');
  el.className = className;
  el.textContent = text;
  return el;
}

export function pinElement(pin: ExplorePin): HTMLButtonElement {
  const button = markerButton(`explore-pin explore-pin--${pin.state}`, pinLabel(pin));
  button.appendChild(pin.state === 'numbered'
    ? span('explore-pin__count', String(pin.count))
    : span('explore-pin__mark', '✦'));
  return button;
}

export function clusterElement(cluster: ExploreCluster): HTMLButtonElement {
  // A different SHAPE, not a bigger pin: a pill carrying the word, where a pin
  // is a disc carrying a bare number. §1.2 — one number must not read as the
  // other, and identical circles made them read alike.
  const button = markerButton('explore-cluster', `${clusterLabel(cluster)}, zoom in`);
  button.appendChild(span('explore-cluster__count', String(cluster.places)));
  button.appendChild(span('explore-cluster__word', cluster.places === 1 ? 'place' : 'places'));
  return button;
}
